import type {
  OfflinePosPersistedState,
  OfflineRefundPayload,
  OfflineSalePayload,
  PendingOp,
} from './types.js';
import { sanitizeRefundPayload, sanitizeSalePayload } from './payload-sanitize.js';

export function createEmptyOfflineState(): OfflinePosPersistedState {
  return {
    version: 2,
    catalog: null,
    pending: [],
    branches: [],
    authProfiles: [],
    salesHistory: [],
    lastOnlineAt: null,
    lastSyncAt: null,
  };
}

export function newClientOpId(kind: 'sale' | 'refund'): string {
  return `${kind}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function enqueuePendingSale(
  state: OfflinePosPersistedState,
  payload: OfflineSalePayload,
  localReceiptId?: string,
): { state: OfflinePosPersistedState; op: PendingOp } {
  const clean = sanitizeSalePayload(payload);
  const op: PendingOp = {
    clientOpId: newClientOpId('sale'),
    kind: 'sale',
    createdAt: new Date().toISOString(),
    payload: clean,
    attempts: 0,
    localReceiptId: localReceiptId || clean.cartId,
  };
  return {
    state: { ...state, pending: [...state.pending, op] },
    op,
  };
}

export function enqueuePendingRefund(
  state: OfflinePosPersistedState,
  payload: OfflineRefundPayload,
): { state: OfflinePosPersistedState; op: PendingOp } {
  const clean = sanitizeRefundPayload(payload);
  const op: PendingOp = {
    clientOpId: newClientOpId('refund'),
    kind: 'refund',
    createdAt: new Date().toISOString(),
    payload: clean,
    attempts: 0,
  };
  return {
    state: { ...state, pending: [...state.pending, op] },
    op,
  };
}

export function removePendingOp(
  state: OfflinePosPersistedState,
  clientOpId: string,
): OfflinePosPersistedState {
  return {
    ...state,
    pending: state.pending.filter((p) => p.clientOpId !== clientOpId),
  };
}

export function markPendingAttempt(
  state: OfflinePosPersistedState,
  clientOpId: string,
  error?: string,
): OfflinePosPersistedState {
  return {
    ...state,
    pending: state.pending.map((p) =>
      p.clientOpId === clientOpId
        ? { ...p, attempts: p.attempts + 1, lastError: error }
        : p,
    ),
  };
}

/** Apply local stock decrement for a sale (best-effort advisory). */
export function applyLocalSaleStockDelta(
  state: OfflinePosPersistedState,
  branchId: string,
  items: Array<{ productId: string; quantity: number }>,
  direction: 'sale' | 'refund',
): OfflinePosPersistedState {
  if (!state.catalog) return state;
  const sign = direction === 'sale' ? -1 : 1;
  const inventories = state.catalog.inventories.map((row) => {
    if (row.branchId !== branchId) return row;
    const hit = items.find((i) => i.productId === row.productId);
    if (!hit) return row;
    return { ...row, stock: row.stock + sign * hit.quantity };
  });
  return {
    ...state,
    catalog: { ...state.catalog, inventories },
  };
}
